import {
  ADDRESS_ERROR_MESSAGES,
  ADDRESS_LENGTH_LIMITS,
  ADDRESS_SCHEMA_LIMITS,
  ADDRESS_REGEX,
  POSTAL_CODE_FORMATS,
  INDIA_COUNTRY_CODE,
  MDS_COUNTRY_CODE,
  getAddressConfigByCountryCode,
  resolvePostalCodeFormat,
} from "constants/addressConfig";
import { KH_VERTICAL_ID } from "constants/config";
import {
  isIsdCodeSelected,
  REQUIRED_ISD_ERROR,
  validateInternationalPhone,
} from "utils/phoneValidation";

const toStr = (value) => (value == null ? "" : String(value));
const trimmed = (value) => toStr(value).trim();

const CONTROL_CHAR_PATTERN =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/;

export const isValidUtf8Text = (value) => {
  const str = toStr(value);
  if (!str) {
    return true;
  }

  if (CONTROL_CHAR_PATTERN.test(str)) {
    return false;
  }

  try {
    const encoded = new TextEncoder().encode(str);
    const decoded = new TextDecoder("utf-8", { fatal: true }).decode(encoded);
    return decoded === str;
  } catch {
    return false;
  }
};

const validateUtf8Text = (value) => {
  if (!toStr(value) || isValidUtf8Text(value)) {
    return null;
  }
  return ADDRESS_ERROR_MESSAGES.INVALID_UTF8;
};

const countCharsExcludingSpaces = (value) =>
  toStr(value).replace(/\s/g, "").length;

const hasLeadingOrTrailingSpaces = (value) => {
  const raw = toStr(value);
  return raw.length > 0 && raw !== raw.trim();
};

const isPositiveNumber = (value) =>
  value != null && value !== "" && Number(value) > 0;

const isWithinRange = (count, { min, max }) => count >= min && count <= max;

const normalizeCountryCode = (countryCode) =>
  String(countryCode || "")
    .trim()
    .toUpperCase();

const setFieldError = (errors, field, error) => {
  if (error) {
    errors[field] = error;
  }
};

const validateNameValue = (value, { required }) => {
  const raw = trimmed(value);
  if (!raw) {
    return required ? ADDRESS_ERROR_MESSAGES.NAME : null;
  }
  if (raw.length > ADDRESS_SCHEMA_LIMITS.NAME_MAX) {
    return ADDRESS_ERROR_MESSAGES.NAME_SCHEMA_MAX;
  }
  if (!ADDRESS_REGEX.NAME.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.NAME_SPECIAL_CHARS;
  }
  if (!isWithinRange(countCharsExcludingSpaces(raw), ADDRESS_LENGTH_LIMITS.NAME)) {
    return ADDRESS_ERROR_MESSAGES.NAME;
  }
  return null;
};

const validateAddressLine1 = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_1;
  }
  if (
    raw.length < ADDRESS_SCHEMA_LIMITS.ADDRESS_LINE_MIN ||
    raw.length > ADDRESS_SCHEMA_LIMITS.ADDRESS_LINE_MAX
  ) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_1;
  }
  if (!ADDRESS_REGEX.ADDRESS_LINE.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_SPECIAL_CHARS;
  }
  if (
    !isWithinRange(
      countCharsExcludingSpaces(raw),
      ADDRESS_LENGTH_LIMITS.ADDRESS_LINE_1
    )
  ) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_1;
  }
  return null;
};

const validateOptionalAddressLine = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return null;
  }
  if (raw.length > ADDRESS_SCHEMA_LIMITS.ADDRESS_LINE_MAX) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_1;
  }
  if (!ADDRESS_REGEX.ADDRESS_LINE.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_SPECIAL_CHARS;
  }
  return null;
};

const validateStreetValue = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return null;
  }
  if (raw.length > ADDRESS_SCHEMA_LIMITS.ADDRESS_LINE_MAX) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_1;
  }
  if (!ADDRESS_REGEX.ADDRESS_LINE.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.ADDRESS_LINE_SPECIAL_CHARS;
  }
  return validateUtf8Text(raw);
};

const validateCityValue = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return ADDRESS_ERROR_MESSAGES.CITY;
  }
  if (raw.length > ADDRESS_SCHEMA_LIMITS.CITY_MAX) {
    return ADDRESS_ERROR_MESSAGES.CITY_SCHEMA_MAX;
  }
  if (!ADDRESS_REGEX.CITY.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.CITY;
  }
  if (!isWithinRange(countCharsExcludingSpaces(raw), ADDRESS_LENGTH_LIMITS.CITY)) {
    return ADDRESS_ERROR_MESSAGES.CITY;
  }
  return null;
};

const validateDistrictName = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return ADDRESS_ERROR_MESSAGES.DISTRICT;
  }
  if (!ADDRESS_REGEX.DISTRICT.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.DISTRICT_SPECIAL_CHARS;
  }
  if (
    !isWithinRange(countCharsExcludingSpaces(raw), ADDRESS_LENGTH_LIMITS.DISTRICT)
  ) {
    return ADDRESS_ERROR_MESSAGES.DISTRICT;
  }
  return null;
};

const validateLandmarkValue = (value) => {
  const raw = trimmed(value);
  if (!raw) {
    return null;
  }
  if (!ADDRESS_REGEX.LANDMARK.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.LANDMARK_SPECIAL_CHARS;
  }
  if (
    !isWithinRange(countCharsExcludingSpaces(raw), ADDRESS_LENGTH_LIMITS.LANDMARK)
  ) {
    return ADDRESS_ERROR_MESSAGES.LANDMARK;
  }
  return null;
};

const validateEmailValue = (value, { required = true } = {}) => {
  const raw = trimmed(value);
  if (!raw) {
    return required ? ADDRESS_ERROR_MESSAGES.EMAIL : null;
  }
  if (!ADDRESS_REGEX.EMAIL.test(raw)) {
    return ADDRESS_ERROR_MESSAGES.EMAIL;
  }
  return null;
};

const validatePostalCodeValue = (value, formatKey) => {
  const format = POSTAL_CODE_FORMATS[formatKey];
  if (!format) {
    return null;
  }
  if (!format.regex.test(trimmed(value))) {
    return format.message;
  }
  return null;
};

export const validateUsaShippingPostalCode = (
  shippingPincode,
  billingPincode,
  useSameAddress = false,
) => {
  const shipping = trimmed(shippingPincode);
  const shippingFormat = resolvePostalCodeFormat(
    MDS_COUNTRY_CODE.USA,
    "shipping",
  );

  if (!validatePostalCodeValue(shipping, shippingFormat)) {
    return null;
  }

  const billing = trimmed(billingPincode);
  const billingFormat = resolvePostalCodeFormat(
    MDS_COUNTRY_CODE.USA,
    "billing",
  );
  if (
    useSameAddress &&
    shipping &&
    billing &&
    shipping === billing &&
    !validatePostalCodeValue(shipping, billingFormat)
  ) {
    return null;
  }

  return POSTAL_CODE_FORMATS.US_ZIP_SHIPPING.message;
};

const validateParentName = (parentName, { isSca, isBilling }) => {
  if (isBilling && !trimmed(parentName)) {
    return isSca
      ? ADDRESS_ERROR_MESSAGES.SCA_PARENT_NAME
      : ADDRESS_ERROR_MESSAGES.NAME;
  }
  return validateNameValue(parentName, { required: isBilling });
};

const validateIndiaPostalLookupFields = (address, config) => {
  const errors = {};

  if (!config.requirePostalCodeId) {
    return errors;
  }

  if (!isPositiveNumber(address.pincodeId)) {
    errors.pincode =
      validatePostalCodeValue(address.pincode, config.postalCodeFormat) ||
      ADDRESS_ERROR_MESSAGES.POSTAL_CODE_6_DIGIT;
  }

  if (config.requireDistrictId && !isPositiveNumber(address.districtId)) {
    errors.district = ADDRESS_ERROR_MESSAGES.DISTRICT_INVALID;
  }

  if (config.requireStateId && !isPositiveNumber(address.stateId)) {
    errors.state = ADDRESS_ERROR_MESSAGES.STATE_REQUIRED;
  }

  return errors;
};

const validateStateName = (state, config) => {
  if (!config.requireStateName) {
    return null;
  }

  const stateValue = trimmed(state);
  if (config.isUsState) {
    return ADDRESS_REGEX.US_STATE.test(stateValue)
      ? null
      : ADDRESS_ERROR_MESSAGES.STATE_US;
  }

  if (!stateValue) {
    return ADDRESS_ERROR_MESSAGES.STATE_REQUIRED;
  }

  return null;
};

const validateScaIndiaStateMatch = ({
  isSca,
  countryCode,
  tenantStateId,
  stateId,
  hasStateError,
  hasPincodeError,
}) => {
  const shouldEnforce =
    isSca &&
    normalizeCountryCode(countryCode) === INDIA_COUNTRY_CODE &&
    tenantStateId != null &&
    !hasStateError &&
    !hasPincodeError;

  if (!shouldEnforce) {
    return null;
  }

  const stateMatchesTenant =
    isPositiveNumber(stateId) && Number(stateId) === Number(tenantStateId);

  if (stateMatchesTenant) {
    return null;
  }

  return ADDRESS_ERROR_MESSAGES.SCA_STATE_MISMATCH;
};

const applyLeadingTrailingSpaceErrors = (errors, address) => {
  ["city", "addressLine2", "street", "recipientName", "parentName"].forEach(
    (field) => {
      if (errors[field] || !hasLeadingOrTrailingSpaces(address[field])) {
        return;
      }
      errors[field] = ADDRESS_ERROR_MESSAGES.LEADING_TRAILING_SPACES;
    }
  );
};

const UTF8_TEXT_FIELDS = [
  "recipientName",
  "parentName",
  "recipientEmail",
  "addressLine1",
  "addressLine2",
  "street",
  "city",
  "landmark",
  "district",
  "state",
  "pincode",
];

const applyUtf8TextErrors = (errors, address) => {
  UTF8_TEXT_FIELDS.forEach((field) => {
    if (errors[field]) {
      return;
    }
    const utf8Error = validateUtf8Text(address[field]);
    if (utf8Error) {
      errors[field] = utf8Error;
    }
  });
};

export const validateAddressByCountry = (address, context = {}) => {
  const {
    isdCode,
    countryCode,
    businessUnitId,
    addressType = "billing",
    tenantStateId,
    billingPostalCode,
    useSameAddress = false,
  } = context;

  const config = getAddressConfigByCountryCode(countryCode);
  const isSca = Number(businessUnitId) === Number(KH_VERTICAL_ID);
  const isBilling = addressType === "billing";
  const a = address || {};
  const errors = {};

  setFieldError(
    errors,
    "recipientName",
    validateNameValue(a.recipientName, { required: true })
  );
  setFieldError(
    errors,
    "parentName",
    validateParentName(a.parentName, { isSca, isBilling })
  );

  if (!isIsdCodeSelected(isdCode)) {
    errors.recipientPhoneNumber = REQUIRED_ISD_ERROR;
  } else if (!validateInternationalPhone(a.recipientPhoneNumber, isdCode)) {
    errors.recipientPhoneNumber = ADDRESS_ERROR_MESSAGES.PHONE;
  }

  setFieldError(
    errors,
    "recipientEmail",
    validateEmailValue(a.recipientEmail, { required: true })
  );
  setFieldError(errors, "addressLine1", validateAddressLine1(a.addressLine1));
  setFieldError(
    errors,
    "addressLine2",
    validateOptionalAddressLine(a.addressLine2)
  );
  setFieldError(errors, "street", validateStreetValue(a.street));
  setFieldError(errors, "city", validateCityValue(a.city));
  setFieldError(errors, "landmark", validateLandmarkValue(a.landmark));

  Object.assign(errors, validateIndiaPostalLookupFields(a, config));

  if (config.requireDistrictName) {
    setFieldError(errors, "district", validateDistrictName(a.district));
  }

  if (config.requirePostalCode) {
    const isUsaShipping =
      !isBilling &&
      normalizeCountryCode(countryCode) === MDS_COUNTRY_CODE.USA;
    setFieldError(
      errors,
      "pincode",
      isUsaShipping
        ? validateUsaShippingPostalCode(
            a.pincode,
            billingPostalCode,
            useSameAddress,
          )
        : validatePostalCodeValue(
            a.pincode,
            resolvePostalCodeFormat(countryCode, addressType),
          ),
    );
  }

  setFieldError(errors, "state", validateStateName(a.state, config));

  setFieldError(
    errors,
    "pincode",
    validateScaIndiaStateMatch({
      isSca,
      countryCode,
      tenantStateId,
      stateId: a.stateId,
      hasStateError: Boolean(errors.state),
      hasPincodeError: Boolean(errors.pincode),
    })
  );

  applyLeadingTrailingSpaceErrors(errors, a);
  applyUtf8TextErrors(errors, a);

  return errors;
};

export const validateOrderAddresses = (orderDetails = {}, context = {}) => {
  const {
    isdCode,
    countryCode,
    businessUnitId,
    tenantStateId,
    hasHardware = false,
    useSameAddress = false,
  } = context;

  const sharedContext = {
    isdCode,
    countryCode,
    businessUnitId,
    tenantStateId,
  };
  const errors = {};

  const billingErrors = validateAddressByCountry(orderDetails.billingAddress, {
    ...sharedContext,
    addressType: "billing",
  });
  if (Object.keys(billingErrors).length > 0) {
    errors.billingAddress = billingErrors;
  }

  const shouldValidateShipping = hasHardware && !useSameAddress;
  if (!shouldValidateShipping) {
    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  }

  const shippingErrors = validateAddressByCountry(
    orderDetails.shippingAddress,
    {
      ...sharedContext,
      addressType: "shipping",
      billingPostalCode: orderDetails.billingAddress?.pincode,
      useSameAddress,
    }
  );
  if (Object.keys(shippingErrors).length > 0) {
    errors.shippingAddress = shippingErrors;
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};
